import { useEffect, useState, type ReactNode } from 'react';

/**
 * Briques de formulaire du module Notes de frais.
 *
 * Vivent dans `src/components/ndf/ui/` et non dans `shared/` : le module doit
 * rester isolable, et ses règles d'ergonomie lui sont propres.
 *
 * Couleurs : charte KESTIO (cf. `src/index.css`). Deux familles à ne jamais
 * mélanger — les tons FONCÉS servent au texte, les PASTELS aux fonds. Un texte
 * corail sur fond corail pastel se lit ; l'inverse non.
 */

export function Champ({
  label,
  erreur,
  aide,
  requis,
  children,
}: {
  label: string;
  erreur?: string | null;
  aide?: ReactNode;
  requis?: boolean;
  children: ReactNode;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-sm font-medium text-nuit">
        {label}
        {requis && <span className="ml-0.5 text-corail">*</span>}
      </span>
      {children}
      {aide && !erreur && <span className="mt-1 block text-xs text-nuit/60">{aide}</span>}
      {erreur && <span className="mt-1 block text-xs font-medium text-corail">{erreur}</span>}
    </label>
  );
}

// min-h-11 = 44 px : la cible tactile minimale. Les écrans d'administration se
// consultent aussi au doigt, et rien ne justifie deux tailles de champs selon
// l'écran où l'on se trouve.
const baseInput =
  'w-full min-h-11 rounded-md border border-nuit/20 bg-white px-3 py-2 text-sm text-nuit ' +
  'focus:border-nuit focus:outline-none focus:ring-2 focus:ring-ciel ' +
  'disabled:cursor-not-allowed disabled:border-transparent disabled:bg-nuit/5 disabled:text-nuit/70';

export function Saisie(props: React.InputHTMLAttributes<HTMLInputElement>) {
  return <input {...props} className={`${baseInput} ${props.className ?? ''}`} />;
}

/**
 * Zone de texte MULTILIGNE, redimensionnable.
 *
 * Tout champ administrable destiné à contenir une phrase ou un paragraphe passe
 * par ici. Sur une seule ligne, un texte juridique de six lignes s'édite par une
 * fenêtre de quarante caractères : on ne voit pas ce qu'on corrige, et on ne
 * relit jamais l'ensemble avant d'enregistrer.
 *
 * `resize-y` et non `resize` : l'élargissement horizontal casserait la grille.
 * Quatre lignes par défaut, la hauteur s'ajuste ensuite à la main.
 */
export function ZoneTexte(props: React.TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return (
    <textarea
      rows={4}
      {...props}
      className={`${baseInput} min-h-24 resize-y py-2 leading-relaxed ${props.className ?? ''}`}
    />
  );
}

export function Selection(props: React.SelectHTMLAttributes<HTMLSelectElement>) {
  return <select {...props} className={`${baseInput} ${props.className ?? ''}`} />;
}

/**
 * Saisie d'un NOMBRE, tapée à la française — avec une virgule.
 *
 * Un champ numérique piloté par un nombre ne se laisse pas taper : « 12, » est
 * converti en 12, la valeur repart dans le champ, et la virgule disparaît sous
 * les doigts. Sur téléphone, l'effet est celui d'un clavier bloqué — on appuie
 * sur la virgule et il ne se passe rien.
 *
 * On garde donc le TEXTE en cours de frappe et on ne le reformate qu'une fois
 * le champ quitté. Tant que l'utilisateur écrit, il voit exactement ce qu'il a
 * tapé ; le formulaire, lui, reçoit un nombre à chaque frappe.
 */
export function SaisieNombre({
  valeur,
  onValeur,
  nullable = false,
  ...props
}: Omit<React.InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange'> & {
  valeur: number | null;
  onValeur: (v: number | null) => void;
  /** Champ facultatif : vidé, il vaut null plutôt que zéro. */
  nullable?: boolean;
}) {
  const afficher = (v: number | null) => (v === null || v === undefined ? '' : String(v).replace('.', ','));
  const [texte, setTexte] = useState(() => afficher(valeur));
  const [enFrappe, setEnFrappe] = useState(false);

  // Hors frappe, le champ suit la valeur : une conversion de devise ou un
  // forçage serveur doit s'y voir immédiatement.
  useEffect(() => {
    if (!enFrappe) setTexte(afficher(valeur));
  }, [valeur, enFrappe]);

  function analyser(t: string): number | null {
    const nettoye = t.replace(',', '.').trim();
    if (nettoye === '') return nullable ? null : 0;
    const n = Number(nettoye);
    // « 12, » vaut 12 le temps qu'on tape la décimale ; une saisie illisible ne
    // doit pas remettre le champ à zéro sous les doigts.
    return Number.isFinite(n) ? n : valeur;
  }

  return (
    <input
      {...props}
      inputMode="decimal"
      value={texte}
      onFocus={(e) => {
        setEnFrappe(true);
        props.onFocus?.(e);
      }}
      onChange={(e) => {
        // Seuls les caractères d'un nombre : sur ordinateur, rien n'empêche
        // sinon de coller « 12 € » dans un champ de montant.
        const t = e.target.value.replace(/[^0-9.,-]/g, '');
        setTexte(t);
        onValeur(analyser(t));
      }}
      onBlur={(e) => {
        setEnFrappe(false);
        setTexte(afficher(analyser(e.target.value)));
        props.onBlur?.(e);
      }}
      className={`${baseInput} ${props.className ?? ''}`}
    />
  );
}

/**
 * Valeur calculée par l'application : fond teinté, pas de bordure de saisie.
 * Sert notamment aux périodes d'essai, que le serveur déduit du statut RH.
 */
export function ValeurCalculee({ children, note }: { children: ReactNode; note?: string }) {
  return (
    <div>
      <div className="min-h-11 rounded-md bg-ciel/25 px-3 py-2 text-sm text-nuit">
        {children || <span className="text-nuit/40">-</span>}
      </div>
      {note && <span className="mt-1 block text-xs text-jaune">{note}</span>}
    </div>
  );
}

export function Case({
  label,
  aide,
  ...props
}: React.InputHTMLAttributes<HTMLInputElement> & { label: string; aide?: string }) {
  // py-2.5 porte la hauteur de la ligne à 44 px : la case elle-même reste
  // petite, mais tout le libellé est cliquable, donc la cible l'est aussi.
  return (
    <label className="flex min-h-11 items-start gap-2 py-2.5">
      <input
        type="checkbox"
        {...props}
        className="mt-0.5 h-4 w-4 rounded border-nuit/30 text-nuit focus:ring-ciel"
      />
      <span>
        <span className="block text-sm text-nuit">{label}</span>
        {aide && <span className="block text-xs text-nuit/60">{aide}</span>}
      </span>
    </label>
  );
}

/**
 * Bascule actif / inactif.
 *
 * Préférée à une case à cocher là où l'état est un STATUT de la donnée, et non
 * un choix du formulaire : on lit l'état sans avoir à interpréter une case
 * vide, et l'action est visible avant d'être faite.
 */
export function Bascule({
  actif,
  onChange,
  libelleActif = 'Actif',
  libelleInactif = 'Inactif',
  disabled,
}: {
  actif: boolean;
  onChange: (actif: boolean) => void;
  libelleActif?: string;
  libelleInactif?: string;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={actif}
      disabled={disabled}
      onClick={() => onChange(!actif)}
      className="flex min-h-11 items-center gap-2 rounded-md px-2 disabled:opacity-50"
    >
      <span
        className={`relative h-5 w-9 shrink-0 rounded-full transition-colors ${
          actif ? 'bg-vert-fond' : 'bg-nuit/25'
        }`}
      >
        <span
          className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-all ${
            actif ? 'left-4.5' : 'left-0.5'
          }`}
        />
      </span>
      <span className={`text-sm ${actif ? 'text-vert' : 'text-nuit/50'}`}>
        {actif ? libelleActif : libelleInactif}
      </span>
    </button>
  );
}

/** Teintes disponibles — une par domaine, jamais décoratives. */
export type TeinteSection = 'ciel' | 'vert' | 'violet' | 'corail' | 'jaune';

/**
 * La couleur porte la BORDURE et un liseré, pas le fond.
 *
 * Un aplat pastel derrière chaque bloc sature l'écran et fait perdre la
 * hiérarchie : tout crie, donc plus rien ne ressort. Le trait coloré suffit à
 * distinguer les blocs, et laisse le contenu respirer sur fond blanc.
 */
const CADRES: Record<TeinteSection, { bordure: string; liseré: string; texte: string }> = {
  ciel: { bordure: 'border-ciel', liseré: 'bg-ciel', texte: 'text-nuit' },
  vert: { bordure: 'border-vert-fond', liseré: 'bg-vert-fond', texte: 'text-vert' },
  violet: { bordure: 'border-violet-fond', liseré: 'bg-violet-fond', texte: 'text-violet' },
  corail: { bordure: 'border-corail-fond', liseré: 'bg-corail-fond', texte: 'text-corail' },
  jaune: { bordure: 'border-jaune-fond', liseré: 'bg-jaune-fond', texte: 'text-jaune' },
};

export function Section({
  titre,
  description,
  teinte = 'ciel',
  actions,
  repliable = false,
  ouvertParDefaut = true,
  resume,
  children,
}: {
  titre: string;
  description?: string;
  teinte?: TeinteSection;
  actions?: ReactNode;
  /** Replie le bloc : on voit tous les titres d'un coup, on n'ouvre que l'utile. */
  repliable?: boolean;
  ouvertParDefaut?: boolean;
  /** Ce qu'on affiche quand le bloc est replié — sinon le titre seul ne dit rien. */
  resume?: ReactNode;
  children: ReactNode;
}) {
  const c = CADRES[teinte];
  const [ouvert, setOuvert] = useState(ouvertParDefaut);
  const deplie = !repliable || ouvert;

  const entete = (
    <div className="flex flex-1 flex-wrap items-start justify-between gap-2 text-left">
      <div>
        <h2 className="flex items-center gap-2 font-titre text-base font-bold text-nuit">
          {repliable && (
            <span
              className={`inline-block transition-transform ${ouvert ? 'rotate-90' : ''}`}
              aria-hidden
            >
              ›
            </span>
          )}
          {titre}
        </h2>
        {description && deplie && (
          <p className="mt-0.5 max-w-3xl text-sm text-nuit/70">{description}</p>
        )}
        {!deplie && resume && <div className="mt-1 text-sm text-nuit/70">{resume}</div>}
      </div>
    </div>
  );

  return (
    <section className={`overflow-hidden rounded-lg border ${c.bordure} bg-white shadow-sm`}>
      {/* Liseré de 3 px : le repère de couleur, sans aplat. */}
      <div className={`h-[3px] ${c.liseré}`} />
      {/* Les ACTIONS sont à CÔTÉ du bouton de repli, jamais dedans.
          Imbriquées, elles produisaient un <button> dans un <button> : le clic
          posait bien l'action ET remontait à l'en-tête, qui repliait la
          section. Le formulaire s'ouvrait invisible, et l'écran paraissait
          avaler le clic (constaté sur le mi-temps thérapeutique). */}
      {repliable ? (
        <div className="flex w-full items-start gap-2 px-4 py-3 sm:px-6">
          <button
            type="button"
            aria-expanded={ouvert}
            onClick={() => setOuvert((v) => !v)}
            className="flex flex-1 items-start gap-2 rounded text-left hover:bg-ciel/15"
          >
            {entete}
          </button>
          {actions}
        </div>
      ) : (
        <header className="flex items-start gap-2 border-b border-nuit/10 px-4 py-3 sm:px-6">
          {entete}
          {actions}
        </header>
      )}
      {deplie && (
        <div className={`space-y-4 p-4 sm:p-6 ${repliable ? 'border-t border-nuit/10' : ''}`}>
          {children}
        </div>
      )}
    </section>
  );
}

/**
 * Bloc secondaire — une catégorie, une liste de valeurs, un compte.
 * La couleur se voit au cadre et à la pastille du titre, pas au fond.
 */
export function Bloc({
  titre,
  teinte = 'ciel',
  barre,
  inactif,
  actions,
  children,
}: {
  titre: ReactNode;
  teinte?: TeinteSection;
  barre?: boolean;
  inactif?: boolean;
  actions?: ReactNode;
  children?: ReactNode;
}) {
  const c = CADRES[teinte];
  return (
    <div
      className={`overflow-hidden rounded-md border ${inactif ? 'border-nuit/15 opacity-60' : c.bordure} bg-white`}
    >
      <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-2">
        <span className="flex items-center gap-2">
          <span
            className={`h-6 w-1 shrink-0 rounded-full ${inactif ? 'bg-nuit/25' : c.liseré}`}
            aria-hidden
          />
          <span
            className={`font-titre text-sm font-bold ${inactif ? 'text-nuit/40' : 'text-nuit'} ${barre ? 'line-through' : ''}`}
          >
            {titre}
          </span>
        </span>
        {actions}
      </div>
      {children}
    </div>
  );
}

/**
 * Une date « AAAA-MM-JJ » affichée à la française.
 *
 * Le format ISO est celui du STOCKAGE et des champs de saisie natifs ; il n'a
 * rien à faire sous les yeux d'un utilisateur français, qui lit 12/12/1969.
 */
export function dateFr(v: string | null | undefined): string {
  if (!v || !/^\d{4}-\d{2}-\d{2}$/.test(v)) return '';
  const [a, m, j] = v.split('-');
  return `${j}/${m}/${a}`;
}

/** Badge — fond pastel, texte foncé de la même famille. */
export function Etiquette({
  children,
  ton = 'neutre',
}: {
  children: ReactNode;
  ton?: 'neutre' | 'actif' | 'positif' | 'alerte' | 'attention';
}) {
  const tons = {
    neutre: 'bg-nuit/10 text-nuit',
    actif: 'bg-ciel/60 text-nuit',
    positif: 'bg-vert-fond/45 text-vert',
    alerte: 'bg-corail-fond/45 text-corail',
    attention: 'bg-jaune-fond/50 text-jaune',
  };
  return (
    <span className={`inline-flex rounded px-1.5 py-0.5 text-xs font-medium ${tons[ton]}`}>
      {children}
    </span>
  );
}

/** Bouton principal — une seule action de ce niveau par écran. */
export function BoutonPrincipal(props: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      {...props}
      // Relief discret : une ombre portée courte et un léger enfoncement au
      // clic suffisent à rendre le bouton « appuyable ». Pas de dégradé.
      className={`min-h-11 rounded-lg bg-nuit px-4 text-sm font-semibold text-white shadow-sm transition-all hover:bg-nuit/90 hover:shadow active:translate-y-px active:shadow-none disabled:opacity-50 disabled:shadow-none ${props.className ?? ''}`}
    />
  );
}

/** Bouton secondaire — ne concurrence jamais visuellement le principal. */
export function BoutonSecondaire(props: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      {...props}
      className={`min-h-11 rounded-lg bg-white px-3 text-sm font-medium text-nuit ring-1 ring-nuit/20 transition-all hover:bg-ciel/20 hover:ring-nuit/30 active:translate-y-px disabled:opacity-50 ${props.className ?? ''}`}
    />
  );
}

/**
 * Un mois (AAAA-MM), remonté À LA FIN de la saisie et non à chaque frappe.
 *
 * Le champ nu remontait chaque caractère au parent, qui s'en sert comme clé de
 * requête : effacer un chiffre de « 2026-09 » lançait un appel sur un mois
 * incomplet, remplaçait l'écran par « Chargement… » puis par « Mois attendu au
 * format AAAA-MM », et rendait le champ inutilisable. On garde donc un tampon
 * local, et on ne remonte qu'un mois complet.
 */
export function ChampMois({
  label = 'Mois',
  valeur,
  onChange,
}: {
  label?: string;
  valeur: string;
  onChange: (mois: string) => void;
}) {
  const [tampon, setTampon] = useState(valeur);
  useEffect(() => setTampon(valeur), [valeur]);
  const complet = (v: string) => /^\d{4}-\d{2}$/.test(v);

  return (
    <Champ label={label}>
      <Saisie
        type="month"
        value={tampon}
        onChange={(e) => {
          const v = e.target.value;
          setTampon(v);
          if (complet(v)) onChange(v);
        }}
        // Une saisie abandonnée en cours de route revient au dernier mois
        // valide : l'écran ne reste jamais sur une valeur qui ne s'affiche pas.
        onBlur={() => {
          if (!complet(tampon)) setTampon(valeur);
        }}
      />
    </Champ>
  );
}
